/**
 * Resolve a notification's destination URL from its kind + meta.
 * Returns null when no meaningful deep-link exists (a null value tells the
 * caller to skip navigation and just mark-read the notification).
 *
 * @param {Object} notif — {kind, meta, ...}
 * @param {string} [role] — viewer's role: "admin" | "super_admin" | "investor" | "startup" | ...
 *                          When absent we return admin defaults.
 */
export function resolveNotifLink(notif, role) {
  if (!notif) return null;
  const rawKind = notif.kind || notif.type || "";
  // Normalise underscore variants for a small, explicit set of prefixes so
  // we don't accidentally rewrite unrelated namespaces (dd_report,
  // founder_premium, mandate_nudge, etc. must stay dotted-as-is).
  const KIND_ALIAS_PREFIXES = ["esign", "kyc", "dealroom", "task", "message",
                               "interest", "query", "match", "assignment"];
  let kind = rawKind;
  for (const p of KIND_ALIAS_PREFIXES) {
    if (kind.startsWith(`${p}_`)) {
      kind = `${p}.${kind.slice(p.length + 1)}`;
      break;
    }
  }
  const meta = notif.meta || {};
  const isAdmin = role === "admin" || role === "super_admin"
    || role === "manager" || role === "agent";
  const isInvestor = role === "investor";
  const isStartup = role === "startup";

  // KYC — admin lands in the Approvals Cockpit drawer; founder/investor
  // go to their own KYC pages instead of an admin-only route.
  if (kind.startsWith("kyc.")) {
    if (isStartup) return "/startup/kyc";
    if (isInvestor) return "/investor/kyc";
    if (meta.investor_id) return `/admin/approvals?tab=investors&entity=${meta.investor_id}`;
    if (meta.startup_id) return `/admin/approvals?tab=startups&entity=${meta.startup_id}`;
    return isAdmin ? "/admin/kyc-queue" : null;
  }

  // Approval / status change on the founder or investor side.
  if (kind.startsWith("startup.status") || kind === "startup.approved") {
    return isStartup ? "/startup/dashboard" : "/admin/startups";
  }
  if (kind.startsWith("investor.status") || kind === "investor.approved") {
    return isInvestor ? "/investor/dashboard" : "/admin/investors";
  }

  // Company intelligence — admin scope only
  if (kind.startsWith("company.verification")) {
    if (!isAdmin) return null;
    if (meta.startup_id) return `/admin/company-intelligence/${meta.startup_id}`;
    return "/admin/company-intelligence";
  }
  if (kind.startsWith("mca.")) {
    return isAdmin ? "/admin/mca-lookup" : null;
  }

  // Deal-room events — everyone can open their own deal room. Fall back to
  // list pages when meta.deal_room_id is missing.
  if (kind.startsWith("esign.") || kind.startsWith("dealroom.")) {
    if (meta.deal_room_id) return `/deal-room/${meta.deal_room_id}`;
    if (isStartup) return "/startup/deal-rooms";
    if (isInvestor) return "/investor/deal-rooms";
    return "/admin/deal-rooms";
  }

  // Investor interest / queries
  if (kind.startsWith("interest.")) {
    if (isInvestor) return "/investor/interests";
    if (isStartup) return "/startup/dashboard";
    return "/admin/interests";
  }
  if (kind.startsWith("query.")) {
    if (isStartup) return "/startup/queries";
    if (isInvestor) return "/investor/queries";
    return "/admin/queries";
  }

  // Tasks + escalations — admin only.
  if (kind.startsWith("task.")) return isAdmin ? "/admin/tasks" : null;

  // Messaging — everyone lands in their inbox.
  if (kind.startsWith("message.")) return "/messages";

  // Match overrides — admin only.
  if (kind.startsWith("match.")) return isAdmin ? "/admin/match-overrides" : null;

  // Diligence reports / financial review — admin sees the review workflow;
  // founder / investor see their own copy of the shared report.
  if (kind.startsWith("dd.") || kind.startsWith("dd_report.") || kind.startsWith("financial.")) {
    if (isStartup) return "/startup/diligence";
    if (isInvestor && meta.startup_id) return `/investor/marketplace/${meta.startup_id}`;
    if (isInvestor) return "/investor/dashboard";
    if (!isAdmin) return null;
    if (meta.startup_id) return `/admin/financial-review/${meta.startup_id}`;
    return "/admin/dd-reports";
  }

  // Monitoring / mandate nudges — admin monitoring; founders land on their dashboard.
  if (kind.startsWith("monitoring.")) {
    if (isStartup) return "/startup/dashboard";
    if (isInvestor) return "/investor/dashboard";
    return isAdmin ? "/admin/monitoring" : null;
  }
  if (kind.startsWith("mandate.")) {
    return isAdmin ? "/admin/mandate-graph" : null;
  }

  // Assignment fanouts land in the Approvals Cockpit.
  if (kind === "assignment.investor" && meta.investor_id && isAdmin) {
    return `/admin/approvals?tab=investors&entity=${meta.investor_id}`;
  }
  if (kind === "assignment.startup" && meta.startup_id && isAdmin) {
    return `/admin/approvals?tab=startups&entity=${meta.startup_id}`;
  }

  return null;
}
